import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, getDocs, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

function fmtDate(value) {
  try {
    const date = value?.toDate ? value.toDate() : value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('en-ZA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  } catch {
    return '—';
  }
}

function sortByCreatedDesc(list) {
  return [...list].sort((a, b) => {
    const aMs = a?.createdAt?.toMillis ? a.createdAt.toMillis() : new Date(a?.createdAt || 0).getTime() || 0;
    const bMs = b?.createdAt?.toMillis ? b.createdAt.toMillis() : new Date(b?.createdAt || 0).getTime() || 0;
    return bMs - aMs;
  });
}

function resolveOrgName(orgData = {}, orgId = '') {
  const candidates = [
    orgData.name,
    orgData.orgName,
    orgData.companyName,
    orgData.businessName,
    orgData.displayName,
    orgData.title,
    orgData.brand?.name,
    orgData.brand?.displayName,
    orgData.profile?.name,
    orgData.settings?.name,
  ];

  const found = candidates.find((value) => typeof value === 'string' && value.trim());
  return found ? found.trim() : orgId || 'Unknown org';
}

function DarkSelect({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-4 py-3 rounded-2xl border border-white/10 bg-transparent text-white min-w-[132px]"
      style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
    >
      <option value="all" style={{ backgroundColor: '#0f172a', color: '#fff' }}>{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value} style={{ backgroundColor: '#0f172a', color: '#fff' }}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function statusTone(status) {
  switch (String(status || '').toLowerCase()) {
    case 'open':
      return 'rgba(59,130,246,0.18)';
    case 'in_progress':
    case 'in progress':
      return 'rgba(56,189,248,0.18)';
    case 'waiting_on_client':
    case 'waiting':
      return 'rgba(245,158,11,0.18)';
    case 'resolved':
      return 'rgba(34,197,94,0.18)';
    case 'closed':
      return 'rgba(148,163,184,0.18)';
    default:
      return 'rgba(255,255,255,0.08)';
  }
}

function priorityTone(priority) {
  switch (String(priority || '').toLowerCase()) {
    case 'critical':
      return 'rgba(244,63,94,0.18)';
    case 'high':
      return 'rgba(249,115,22,0.18)';
    case 'normal':
      return 'rgba(255,255,255,0.08)';
    case 'low':
      return 'rgba(34,197,94,0.15)';
    default:
      return 'rgba(255,255,255,0.08)';
  }
}

function messageTone(senderType) {
  switch (String(senderType || '').toLowerCase()) {
    case 'support':
      return {
        backgroundColor: 'rgba(16,185,129,0.14)',
        borderColor: 'rgba(16,185,129,0.32)',
      };
    case 'client':
    default:
      return {
        backgroundColor: 'rgba(59,130,246,0.15)',
        borderColor: 'rgba(59,130,246,0.32)',
      };
  }
}

function statusCount(list, target) {
  return list.filter((ticket) => String(ticket.status || '').toLowerCase() === target).length;
}

function StarRow({ value }) {
  return (
    <div className="flex items-center gap-1 text-xl" aria-label={`${value || 0} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} style={{ color: star <= Number(value || 0) ? '#fbbf24' : 'rgba(255,255,255,0.18)' }}>
          ★
        </span>
      ))}
    </div>
  );
}

export default function SupportDeskPage() {
  const [tickets, setTickets] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [messages, setMessages] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [reply, setReply] = useState('');
  const [statusDraft, setStatusDraft] = useState('open');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    let orgUnsubs = [];

    async function load() {
      try {
        setError('');
        const orgsSnap = await getDocs(collection(db, 'orgs'));
        if (!active) return;

        const ticketMap = new Map();

        orgUnsubs = orgsSnap.docs.map((orgDoc) => {
          const orgId = orgDoc.id;
          const orgData = orgDoc.data() || {};
          const resolvedOrgName = resolveOrgName(orgData, orgId);
          const ticketsRef = collection(db, 'orgs', orgId, 'supportTickets');
          const q = query(ticketsRef, orderBy('createdAt', 'desc'));

          return onSnapshot(
            q,
            (snap) => {
              snap.docChanges().forEach((change) => {
                const id = change.doc.id;
                if (change.type === 'removed') {
                  ticketMap.delete(id);
                } else {
                  const ticketData = change.doc.data() || {};
                  ticketMap.set(id, {
                    ...ticketData,
                    id,
                    ref: change.doc.ref,
                    orgId,
                    orgName: resolvedOrgName,
                  });
                }
              });

              if (!active) return;
              const next = sortByCreatedDesc(Array.from(ticketMap.values()));
              setTickets(next);
              setSelectedId((prev) => prev || next[0]?.id || '');
            },
            (err) => {
              console.error('[SupportDeskPage] org tickets failed', orgId, err);
              if (active) setError('Could not load support tickets right now.');
            }
          );
        });
      } catch (err) {
        console.error('[SupportDeskPage] load failed', err);
        if (active) setError('Could not load support tickets right now.');
      }
    }

    load();

    return () => {
      active = false;
      orgUnsubs.forEach((fn) => {
        try {
          fn && fn();
        } catch {
          // ignore unsubscribe errors
        }
      });
    };
  }, []);

  const orgOptions = useMemo(() => {
    const map = new Map();
    tickets.forEach((ticket) => map.set(ticket.orgId || 'unknown', ticket.orgName || ticket.orgId || 'Unknown org'));
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [tickets]);

  const filteredTickets = useMemo(() => {
    return tickets.filter((ticket) => {
      const statusOk = statusFilter === 'all' || String(ticket.status || '').toLowerCase() === statusFilter;
      const priorityOk = priorityFilter === 'all' || String(ticket.priority || '').toLowerCase() === priorityFilter;
      const orgOk = orgFilter === 'all' || String(ticket.orgId || '') === orgFilter;
      return statusOk && priorityOk && orgOk;
    });
  }, [tickets, statusFilter, priorityFilter, orgFilter]);

  useEffect(() => {
    if (!filteredTickets.some((ticket) => ticket.id === selectedId)) {
      setSelectedId(filteredTickets[0]?.id || '');
    }
  }, [filteredTickets, selectedId]);

  const selected = useMemo(
    () => filteredTickets.find((ticket) => ticket.id === selectedId) || tickets.find((ticket) => ticket.id === selectedId) || null,
    [filteredTickets, selectedId, tickets]
  );

  useEffect(() => {
    if (!selected?.ref) {
      setMessages([]);
      setStatusDraft('open');
      return undefined;
    }
    setStatusDraft(selected.status || 'open');
    const q = query(collection(selected.ref, 'messages'), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => setMessages(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))),
      (err) => {
        console.error('[SupportDeskPage] messages failed', err);
        setMessages([]);
      }
    );
    return () => unsub();
  }, [selected?.ref, selected?.status]);

  const openCount = statusCount(tickets, 'open');
  const inProgressCount = statusCount(tickets, 'in_progress');
  const waitingCount = statusCount(tickets, 'waiting_on_client');
  const resolvedCount = statusCount(tickets, 'resolved');

  const ratedTickets = tickets.filter((ticket) => Number(ticket?.rating?.stars || 0) > 0);
  const averageRating = ratedTickets.length
    ? (ratedTickets.reduce((sum, ticket) => sum + Number(ticket.rating.stars || 0), 0) / ratedTickets.length).toFixed(1)
    : null;

  async function saveUpdate() {
    if (!selected?.ref) return;
    setSaving(true);
    try {
      if (reply.trim()) {
        await addDoc(collection(selected.ref, 'messages'), {
          senderType: 'support',
          senderName: 'Invoice Mate Support',
          body: reply.trim(),
          createdAt: serverTimestamp(),
        });
      }
      await updateDoc(selected.ref, {
        status: statusDraft,
        updatedAt: serverTimestamp(),
        lastReplyAt: serverTimestamp(),
      });
      setReply('');
      setError('');
    } catch (err) {
      console.error('[SupportDeskPage] save failed', err);
      setError('Could not save the support update right now.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="super-support-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-4xl font-bold">Support Desk</h2>
          <p className="text-white/70 mt-2">Manage Neo Admin support tickets, reply to stores, update status, and track support ratings.</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <DarkSelect
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="All statuses"
            options={[
              { value: 'open', label: 'Open' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'waiting_on_client', label: 'Waiting on Client' },
              { value: 'resolved', label: 'Resolved' },
              { value: 'closed', label: 'Closed' },
            ]}
          />
          <DarkSelect
            value={priorityFilter}
            onChange={setPriorityFilter}
            placeholder="All priorities"
            options={[
              { value: 'low', label: 'Low' },
              { value: 'normal', label: 'Normal' },
              { value: 'high', label: 'High' },
              { value: 'critical', label: 'Critical' },
            ]}
          />
          <DarkSelect value={orgFilter} onChange={setOrgFilter} placeholder="All orgs" options={orgOptions} />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
        {[
          { label: 'Open', value: openCount, tone: 'rgba(59,130,246,0.12)' },
          { label: 'In Progress', value: inProgressCount, tone: 'rgba(56,189,248,0.12)' },
          { label: 'Waiting', value: waitingCount, tone: 'rgba(245,158,11,0.12)' },
          { label: 'Resolved', value: resolvedCount, tone: 'rgba(34,197,94,0.12)' },
          { label: 'Avg Rating', value: averageRating ? `${averageRating} ★` : '—', tone: 'rgba(251,191,36,0.10)' },
        ].map((item) => (
          <div key={item.label} className="rounded-2xl border border-white/10 px-4 py-3" style={{ backgroundColor: item.tone }}>
            <div className="text-xs uppercase tracking-[0.2em] text-white/60">{item.label}</div>
            <div className="text-2xl font-bold mt-1">{item.value}</div>
          </div>
        ))}
      </div>

      {error ? (
        <div className="mt-6 rounded-2xl border px-4 py-3 text-rose-200" style={{ backgroundColor: 'rgba(127,29,29,0.35)', borderColor: 'rgba(244,63,94,0.25)' }}>
          {error}
        </div>
      ) : null}

      <div className="grid lg:grid-cols-[370px_minmax(0,1fr)] gap-5 mt-6">
        <section className="rounded-3xl border border-white/10 overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
          <div className="px-4 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="font-semibold">Tickets</div>
            <div className="text-sm text-white/50">{filteredTickets.length} shown</div>
          </div>
          <div className="max-h-[620px] overflow-auto">
            {filteredTickets.length === 0 ? <div className="p-4 text-white/60">No tickets found for the current filters.</div> : null}
            {filteredTickets.map((ticket) => {
              const active = ticket.id === selectedId;
              return (
                <button
                  key={ticket.id}
                  type="button"
                  onClick={() => setSelectedId(ticket.id)}
                  className="w-full text-left px-4 py-4 border-b border-white/5 transition"
                  style={{ backgroundColor: active ? 'rgba(56,189,248,0.10)' : 'transparent' }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-semibold">{ticket.ticketNumber || ticket.id}</div>
                    <span className="px-3 py-1 rounded-full text-[11px] border border-white/10 uppercase" style={{ backgroundColor: statusTone(ticket.status) }}>
                      {String(ticket.status || 'open').replaceAll('_', ' ')}
                    </span>
                  </div>
                  <div className="mt-2 text-white/90">{ticket.topic || 'No topic'}</div>
                  <div className="mt-1 text-sm text-white/60">{ticket.orgName || ticket.orgId || 'Unknown org'}</div>
                  <div className="mt-1 text-sm text-white/60">{ticket.area || 'General'} • {ticket.priority || 'Normal'}</div>
                  {ticket.rating?.stars ? (
                    <div className="mt-2 flex items-center gap-2 text-sm text-amber-300">
                      <StarRow value={ticket.rating.stars} />
                    </div>
                  ) : null}
                  <div className="mt-2 text-xs text-white/45">{fmtDate(ticket.createdAt)}</div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="rounded-3xl border border-white/10 p-5" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
          {!selected ? (
            <div className="text-white/65">Select a ticket to review it.</div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-cyan-300 text-sm font-semibold tracking-[0.2em]">REFERENCE</div>
                  <div className="text-3xl font-bold mt-1">{selected.ticketNumber || selected.id}</div>
                  <div className="text-xl mt-2">{selected.topic || 'No topic'}</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <span className="px-3 py-1 rounded-full text-xs border border-white/10 uppercase" style={{ backgroundColor: statusTone(selected.status) }}>
                    {String(selected.status || 'open').replaceAll('_', ' ')}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs border border-white/10 uppercase" style={{ backgroundColor: priorityTone(selected.priority) }}>
                    {selected.priority || 'Normal'}
                  </span>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-3">
                <div className="rounded-2xl border border-white/10 p-4" style={{ backgroundColor: 'rgba(0,0,0,0.12)' }}>
                  <div className="text-xs text-white/50 mb-2">ORG</div>
                  <div className="text-lg">{selected.orgName || selected.orgId || 'Unknown org'}</div>
                </div>
                <div className="rounded-2xl border border-white/10 p-4" style={{ backgroundColor: 'rgba(0,0,0,0.12)' }}>
                  <div className="text-xs text-white/50 mb-2">AREA</div>
                  <div className="text-lg">{selected.area || 'General'}</div>
                </div>
                <div className="rounded-2xl border border-white/10 p-4" style={{ backgroundColor: 'rgba(0,0,0,0.12)' }}>
                  <div className="text-xs text-white/50 mb-2">CREATED</div>
                  <div className="text-lg">{fmtDate(selected.createdAt)}</div>
                </div>
              </div>

              <div>
                <div className="text-xs text-white/50 mb-2">ORIGINAL REQUEST</div>
                <div className="rounded-2xl border border-white/10 p-4 min-h-[84px]" style={{ backgroundColor: 'rgba(0,0,0,0.12)' }}>
                  {selected.description || '—'}
                </div>
              </div>

              {selected.rating?.stars ? (
                <div className="rounded-2xl border border-amber-400/20 p-4" style={{ backgroundColor: 'rgba(251,191,36,0.08)' }}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-sm uppercase tracking-[0.18em] text-amber-300/90">Client Rating</div>
                      <div className="text-white/70 mt-1">Rated by {selected.rating?.ratedByName || 'Client'} on {fmtDate(selected.rating?.ratedAt)}</div>
                    </div>
                    <StarRow value={selected.rating.stars} />
                  </div>
                  {selected.rating?.comment ? <div className="mt-3 text-white/90 whitespace-pre-wrap">“{selected.rating.comment}”</div> : null}
                </div>
              ) : ['resolved', 'closed'].includes(String(selected.status || '').toLowerCase()) ? (
                <div className="rounded-2xl border border-white/10 p-4 text-white/65" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                  Waiting for client rating.
                </div>
              ) : null}

              <div>
                <div className="font-semibold mb-3">Conversation</div>
                <div className="space-y-3 max-h-[260px] overflow-auto pr-1">
                  {messages.length === 0 ? <div className="text-white/60">No replies yet.</div> : null}
                  {messages.map((message) => {
                    const tone = messageTone(message.senderType);
                    return (
                      <div key={message.id} className="rounded-2xl border p-4" style={tone}>
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <div className="font-semibold">{message.senderName || (message.senderType === 'client' ? 'Client' : 'Support')}</div>
                          <div className="text-xs text-white/50">{fmtDate(message.createdAt)}</div>
                        </div>
                        <div className="text-white/90 whitespace-pre-wrap">{message.body || '—'}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid md:grid-cols-[220px_minmax(0,1fr)] gap-3 items-start">
                <DarkSelect
                  value={statusDraft}
                  onChange={setStatusDraft}
                  placeholder="Status"
                  options={[
                    { value: 'open', label: 'Open' },
                    { value: 'in_progress', label: 'In Progress' },
                    { value: 'waiting_on_client', label: 'Waiting on Client' },
                    { value: 'resolved', label: 'Resolved' },
                    { value: 'closed', label: 'Closed' },
                  ]}
                />
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={4}
                  placeholder="Write a support reply..."
                  className="w-full rounded-2xl border border-white/10 px-4 py-3 bg-transparent text-white outline-none"
                  style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={saveUpdate}
                  disabled={saving}
                  className="px-5 py-3 rounded-2xl font-semibold text-slate-950 disabled:opacity-60"
                  style={{ backgroundColor: '#67e8f9' }}
                >
                  {saving ? 'Saving...' : 'Save Update'}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </section>
  );
}
